import { Component } from '@theme/component';

/**
 * A thin wrapper around `<predictive-search-component>` that expands and
 * collapses the hero search results panel like a combobox popover.
 *
 * The nested predictive search component owns fetching, keyboard navigation,
 * and resetting the query; this element only manages the expanded state.
 *
 * @extends {Component}
 */
class HeroSearchComponent extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    // A fresh controller per connect keeps the element functional after a
    // disconnect/reconnect cycle (e.g. the theme editor moving nodes).
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('focusin', this.#expand, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('click', this.#handleInsideClick, { signal });
    document.addEventListener('pointerdown', this.#handlePointerDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Gets the search input of the nested predictive search component.
   * @returns {HTMLInputElement | null} The search input.
   */
  get #searchInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="search"]'));
  }

  #expand = () => {
    this.setAttribute('expanded', '');
    this.#searchInput?.setAttribute('aria-expanded', 'true');
  };

  #collapse = () => {
    this.removeAttribute('expanded');
    this.#searchInput?.setAttribute('aria-expanded', 'false');
  };

  /**
   * Collapses the panel when focus moves outside the component. Clicks on
   * non-focusable areas produce a null relatedTarget and are ignored here;
   * the document pointerdown listener handles true outside presses.
   * @param {FocusEvent} event - The focus event.
   */
  #handleFocusOut = (event) => {
    const { relatedTarget } = event;
    if (relatedTarget instanceof Node && !this.contains(relatedTarget)) this.#collapse();
  };

  /**
   * Collapses the panel when a pointer press lands outside the component.
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerDown = (event) => {
    if (!this.hasAttribute('expanded')) return;
    if (event.target instanceof Node && !this.contains(event.target)) this.#collapse();
  };

  /**
   * Keeps focus on the search input when clicking non-interactive areas of
   * the panel, mirroring the search modal's behavior (its click-to-refocus
   * listener is only attached when a dialog ancestor exists).
   * @param {MouseEvent} event - The mouse event.
   */
  #handleInsideClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('button, a, input')) return;
    this.#searchInput?.focus();
  };

  /**
   * Collapses the panel on Escape. The nested predictive search component's
   * own Escape handler resets the query; both are meant to run.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.#collapse();
    this.#searchInput?.blur();
  };
}

if (!customElements.get('hero-search-component')) {
  customElements.define('hero-search-component', HeroSearchComponent);
}
